import { InputHTMLAttributes, forwardRef, useId } from 'react';

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: React.ReactNode;
    error?: string;
    helperText?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
    ({ label, error, helperText, className = '', id, ...props }, ref) => {
        const generatedId = useId();
        const checkboxId = id || generatedId;

        const checkboxClasses = `
            h-4 w-4 rounded border-gray-300 dark:border-gray-600
            accent-accent focus:ring-accent focus:ring-2 focus:ring-offset-0
            disabled:opacity-50 disabled:cursor-not-allowed
            bg-white dark:bg-gray-800
            dark:focus:ring-offset-dark
            ${error ? 'border-red-500' : ''}
        `;

        const hasExtras = label || error || helperText;

        if (!hasExtras) {
            return (
                <input
                    ref={ref}
                    type="checkbox"
                    id={checkboxId}
                    className={`${checkboxClasses} ${className}`}
                    {...props}
                />
            );
        }

        return (
            <div className="w-full">
                <div className="flex items-start">
                    <input
                        ref={ref}
                        type="checkbox"
                        id={checkboxId}
                        className={`${checkboxClasses} mt-0.5 shrink-0 ${className}`}
                        {...props}
                    />
                    {label && (
                        <label
                            htmlFor={checkboxId}
                            className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
                        >
                            {label}
                        </label>
                    )}
                </div>
                {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
                {helperText && !error && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
                )}
            </div>
        );
    },
);

Checkbox.displayName = 'Checkbox';
